import { ComponentType } from 'react';
import { CardDefinition, CardUserContext } from '@/types/cards';

/**
 * Props that all card components must accept
 */
export interface CardComponentProps {
  card: CardDefinition;
  userContext: CardUserContext;
  pageId?: string;
  onError?: (error: Error) => void;
}

/**
 * Card registry for mapping cardIds to their rendering components
 */
class CardRegistry {
  private registry: Map<string, ComponentType<CardComponentProps>> = new Map();

  /**
   * Register a card component
   * @param cardId - The cardId to register
   * @param component - The React component that renders this card
   */
  register(cardId: string, component: ComponentType<CardComponentProps>): void {
    this.registry.set(cardId, component);
  }

  /**
   * Get a registered card component
   * @param cardId - The cardId to look up
   * @returns The component if found, undefined otherwise
   */
  get(cardId: string): ComponentType<CardComponentProps> | undefined {
    return this.registry.get(cardId);
  }

  /**
   * Check if a card is registered
   */
  has(cardId: string): boolean {
    return this.registry.has(cardId);
  }

  /**
   * Register multiple cards at once
   */
  registerBulk(
    cards: Record<string, ComponentType<CardComponentProps>>
  ): void {
    Object.entries(cards).forEach(([cardId, component]) => {
      this.register(cardId, component);
    });
  }

  /**
   * Get all registered cardIds
   */
  getRegisteredIds(): string[] {
    return Array.from(this.registry.keys());
  }
}

// Singleton instance
let registryInstance: CardRegistry | null = null;

/**
 * Get the global card registry instance
 */
export function getCardRegistry(): CardRegistry {
  if (!registryInstance) {
    registryInstance = new CardRegistry();
  }
  return registryInstance;
}

/**
 * Reset the registry (used mainly for testing)
 */
export function resetCardRegistry(): void {
  registryInstance = null;
}

export default CardRegistry;
